import { useCallback, useEffect, useRef, useState } from 'react';

type UseUnsavedChangesGuardArgs = {
  isDirty: boolean;
  onModalShown: () => void;
  onDiscardConfirmed: () => void;
  onDismissed: () => void;
};

export type UseUnsavedChangesGuardReturn = {
  isModalOpen: boolean;
  guardNavigation: (navigateAction: () => void) => void;
  confirmDiscard: () => void;
  dismiss: () => void;
};

export const useUnsavedChangesGuard = ({
  isDirty,
  onModalShown,
  onDiscardConfirmed,
  onDismissed,
}: UseUnsavedChangesGuardArgs): UseUnsavedChangesGuardReturn => {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const isDirtyRef = useRef(isDirty);
  const pendingNavigationRef = useRef<(() => void) | null>(null);
  const suppressNextPopStateRef = useRef(false);

  isDirtyRef.current = isDirty;

  const guardNavigation = useCallback(
    (navigateAction: () => void) => {
      if (!isDirtyRef.current) {
        navigateAction();

        return;
      }
      pendingNavigationRef.current = navigateAction;
      setIsModalOpen(true);
      onModalShown();
    },
    [onModalShown]
  );

  // Kept in a ref so the popstate effect below only depends on isDirty, not on guardNavigation's
  // identity — otherwise an unmemoized caller callback would re-arm the sentinel every render.
  const guardNavigationRef = useRef(guardNavigation);

  guardNavigationRef.current = guardNavigation;

  const confirmDiscard = useCallback(() => {
    const navigateAction = pendingNavigationRef.current;

    pendingNavigationRef.current = null;
    setIsModalOpen(false);
    onDiscardConfirmed();
    navigateAction?.();
  }, [onDiscardConfirmed]);

  const dismiss = useCallback(() => {
    pendingNavigationRef.current = null;
    setIsModalOpen(false);
    onDismissed();
  }, [onDismissed]);

  // useBlocker needs a data router, which this app doesn't use — so instead we push a duplicate
  // same-URL history entry while dirty; the first back press just pops that duplicate and fires
  // popstate, which we intercept to show the confirmation modal instead of the real navigation.
  useEffect(() => {
    if (!isDirty) {
      return undefined;
    }

    window.history.pushState(null, '', window.location.href);

    const handlePopState = () => {
      if (suppressNextPopStateRef.current) {
        suppressNextPopStateRef.current = false;

        return;
      }
      if (!isDirtyRef.current) {
        return;
      }

      window.history.pushState(null, '', window.location.href);
      guardNavigationRef.current(() => {
        suppressNextPopStateRef.current = true;
        // -2, not -1: the initial sentinel push plus this handler's own re-arm push both sit
        // between here and the real previous page (each dismissed press nets zero extra depth).
        window.history.go(-2);
      });
    };

    window.addEventListener('popstate', handlePopState);

    return () => window.removeEventListener('popstate', handlePopState);
  }, [isDirty]);

  return { isModalOpen, guardNavigation, confirmDiscard, dismiss };
};
